'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import {
  AGENT_EXECUTION_BULK_MAX_IDS,
  type AgentExecutionDto,
} from '@terra/shared-contracts-agent-execution';
import { agentExecutionClient } from '@terra/shared-rest-clients-agent-execution';
import {
  chunkIds,
  isAbortableAgentExecution,
} from './agent-executions-list.model';

const toErrorLog = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));

export function useAgentExecutionsActions({
  targetId,
  getSelectedExecutions,
  onComplete,
  onSelectionCleared,
}: {
  targetId: string | null;
  getSelectedExecutions: () => Promise<AgentExecutionDto[]>;
  onComplete: () => void;
  onSelectionCleared: () => void;
}) {
  const tAgent = useTranslations('agentExecutions');
  const tBulkActions = useTranslations('proxy.executions.bulkActions');
  const [abortingIds, setAbortingIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [pendingDelete, setPendingDelete] = useState<AgentExecutionDto[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkAborting, setIsBulkAborting] = useState(false);
  const [isResolvingSelection, setIsResolvingSelection] = useState(false);

  const runChunked = useCallback(
    async ({
      ids,
      action,
    }: {
      ids: readonly string[];
      action: 'abort' | 'delete';
    }) => {
      if (!targetId) {
        return;
      }

      for (const executionIds of chunkIds({
        ids,
        size: AGENT_EXECUTION_BULK_MAX_IDS,
      })) {
        const request = { params: { targetId }, body: { executionIds } };
        const response =
          action === 'abort'
            ? await agentExecutionClient.bulkAbort(request)
            : await agentExecutionClient.bulkDelete(request);

        if (response.status !== 200) {
          throw new Error(`Failed to ${action} agent executions`);
        }
      }
    },
    [targetId],
  );

  const resolveSelection = useCallback(async () => {
    setIsResolvingSelection(true);

    try {
      return await getSelectedExecutions();
    } finally {
      setIsResolvingSelection(false);
    }
  }, [getSelectedExecutions]);

  const abortExecution = useCallback(
    async (execution: AgentExecutionDto) => {
      setAbortingIds((current) => new Set([...current, execution.id]));

      try {
        await agentExecutionClient.abort({
          params: { executionId: execution.id },
          body: {},
        });
        onComplete();
      } catch (error: unknown) {
        console.error('Failed to abort execution:', toErrorLog(error));
        toasterApi.error({
          title: tAgent('abortErrorTitle'),
          description: tAgent('abortErrorDescription'),
        });
      } finally {
        setAbortingIds(
          (current) =>
            new Set([...current].filter((id) => id !== execution.id)),
        );
      }
    },
    [onComplete, tAgent],
  );

  const bulkAbort = useCallback(async () => {
    setIsBulkAborting(true);

    try {
      const abortable = (await resolveSelection()).filter(
        isAbortableAgentExecution,
      );

      if (abortable.length === 0) {
        toasterApi.warning({ title: tBulkActions('noEligibleExecutions') });

        return;
      }

      await runChunked({
        ids: abortable.map((execution) => execution.id),
        action: 'abort',
      });
      onSelectionCleared();
      onComplete();
    } catch (error: unknown) {
      console.error('Failed to abort executions:', toErrorLog(error));
      toasterApi.error({
        title: tAgent('abortErrorTitle'),
        description: tAgent('abortErrorDescription'),
      });
    } finally {
      setIsBulkAborting(false);
    }
  }, [
    onComplete,
    onSelectionCleared,
    resolveSelection,
    runChunked,
    tAgent,
    tBulkActions,
  ]);

  const requestDelete = useCallback((execution: AgentExecutionDto) => {
    setPendingDelete([execution]);
  }, []);

  const requestBulkDelete = useCallback(async () => {
    try {
      const selected = await resolveSelection();

      if (selected.length > 0) {
        setPendingDelete(selected);
      }
    } catch (error: unknown) {
      console.error(
        'Failed to resolve selected executions:',
        toErrorLog(error),
      );
      toasterApi.error({
        title: tAgent('deleteErrorTitle'),
        description: tAgent('deleteErrorDescription'),
      });
    }
  }, [resolveSelection, tAgent]);

  const confirmDelete = useCallback(async () => {
    setIsDeleting(true);

    try {
      await runChunked({
        ids: pendingDelete.map((execution) => execution.id),
        action: 'delete',
      });
      toasterApi.success({
        title: tAgent('deleteSuccessTitle'),
        description: tAgent('deleteSuccessDescription'),
      });
      setPendingDelete([]);
      onSelectionCleared();
      onComplete();
    } catch (error: unknown) {
      console.error('Failed to delete executions:', toErrorLog(error));
      toasterApi.error({
        title: tAgent('deleteErrorTitle'),
        description: tAgent('deleteErrorDescription'),
      });
    } finally {
      setIsDeleting(false);
    }
  }, [onComplete, onSelectionCleared, pendingDelete, runChunked, tAgent]);

  return {
    abortingIds,
    abortExecution,
    bulkAbort,
    isBulkAborting,
    requestDelete,
    requestBulkDelete,
    confirmDelete,
    cancelDelete: () => setPendingDelete([]),
    pendingDeleteCount: pendingDelete.length,
    isDeleting,
    isBusy: isResolvingSelection || isBulkAborting || isDeleting,
  };
}
